import { logger } from "@mgdx-libs/logger";
import * as Sentry from "@sentry/react";
import StackdriverErrorReporter from "stackdriver-errors-js";

import { apiResponseStatusToError, ApiTimeoutError } from "../errors";
import type { TypeErrorWithRequestInfo } from "../types";
import { localErrorHandler } from "../utils/localErrorHandler";
import { toApiErrorDetailMessage, toStatusUrlMessage } from "../utils/responseToErrorMessage";
import {
  isExpectedErrorForSentry,
  isNetworkError,
  isSentryEnabled,
  normalizeApiEndpoint,
  sanitizeUrl,
} from "../utils/sentry";

// 送信枠のキーや Sentry の Issue の識別に使うため、固定の値だけを受け付ける
export type CallLimitGroup = "ai-assistant";

export type ErrorReporter = (
  error: string | Error | Response | DOMException,
  options?: {
    skipLocalFrames?: number;
    skipSentry?: boolean;
    extra?: Record<string, unknown>;
    tags?: Record<string, string>;
    // 指定した機能の報告を専用の送信枠で数え、他のエラーの送信枠を圧迫しないようにする。
    // 指定した場合、Sentry で除外している通信断も件数を数えられるよう info で送る
    callLimitGroup?: CallLimitGroup;
  }
) => Promise<void>;

export type CreateErrorHandler = () => ErrorReporter;

const CALL_LIMIT = 50;
const TIME_FRAME = 60 * 60 * 1000;
const CALL_COUNT_SESSION_KEY = "callLimit";
const FIRST_CALL_TIME_SESSION_KEY = "firstCallTime";
// 想定内のエラーが上限を使い切って、それ以外のエラーが送れなくならないよう計数を分ける
const EXPECTED_ERROR_SESSION_KEY_SUFFIX = ":expected";
// 侵入検知に使う 403 が他のエラーに押し出されないよう専用の枠にする
const FORBIDDEN_ERROR_SESSION_KEY_SUFFIX = ":forbidden";

const isResponse = (error: unknown): error is Response => typeof Response !== "undefined" && error instanceof Response;

// 生の Response を送ると Sentry 上で中身が読めないため、status に応じた Error へ変換する
const toReportableError = async (error: string | Error | Response | DOMException): Promise<string | Error> => {
  if (isResponse(error)) {
    let detail = toStatusUrlMessage(error);
    if (!error.bodyUsed) {
      try {
        detail = toApiErrorDetailMessage(error, await error.clone().json());
      } catch {
        // clone できない・本文が JSON でない場合は status と url だけ残す
      }
    }
    return apiResponseStatusToError(error, detail);
  }

  if (typeof DOMException !== "undefined" && error instanceof DOMException && error.name === "AbortError") {
    return new ApiTimeoutError(error);
  }

  return error;
};

// Stackdriver は extra を送らず name と message と stack だけを送るため、fetch 層で付けた通信の状況を message に含める。
// Sentry の除外判定は元の message で行うため、Stackdriver に送る分だけを作り直す
const toStackdriverError = (error: string | Error): string | Error => {
  if (!(error instanceof TypeError)) return error;

  const { requestUrl, apiName, method, elapsedMs, online, visibilityState } = error as TypeErrorWithRequestInfo;
  const requestInfo = [
    apiName !== undefined && `api: ${apiName}`,
    method !== undefined && `method: ${method}`,
    // パスにも患者 ID や薬剤コードが入りうるため、エンドポイントの種類が分かる形に置き換える
    requestUrl !== undefined && `endpoint: ${normalizeApiEndpoint(requestUrl) ?? sanitizeUrl(requestUrl)}`,
    elapsedMs !== undefined && `elapsedMs: ${elapsedMs}`,
    online !== undefined && `online: ${online}`,
    visibilityState !== undefined && `visibility: ${visibilityState}`,
  ].filter(Boolean);
  if (requestInfo.length === 0) return error;

  const reportableError = new TypeError(`${error.message} (${requestInfo.join(", ")})`);
  reportableError.stack = error.stack;
  return reportableError;
};

type CallCounter = { firstCallTime: number; count: number };

type CallCounterStore = {
  get: (keySuffix: string) => CallCounter;
  set: (keySuffix: string, counter: CallCounter) => void;
};

const sessionCallCounterStore: CallCounterStore = {
  get: (keySuffix) => ({
    firstCallTime: Number(sessionStorage.getItem(`${FIRST_CALL_TIME_SESSION_KEY}${keySuffix}`)) || 0,
    count: Number(sessionStorage.getItem(`${CALL_COUNT_SESSION_KEY}${keySuffix}`)) || 0,
  }),
  set: (keySuffix, { firstCallTime, count }) => {
    sessionStorage.setItem(`${FIRST_CALL_TIME_SESSION_KEY}${keySuffix}`, firstCallTime.toString());
    sessionStorage.setItem(`${CALL_COUNT_SESSION_KEY}${keySuffix}`, count.toString());
  },
};

// サイトデータのブロックなどで sessionStorage に触れない環境でも上限を効かせるため、メモリ上で計数する
const memoryCallCounters = new Map<string, CallCounter>();

const memoryCallCounterStore: CallCounterStore = {
  get: (keySuffix) => memoryCallCounters.get(keySuffix) ?? { firstCallTime: 0, count: 0 },
  set: (keySuffix, counter) => {
    memoryCallCounters.set(keySuffix, counter);
  },
};

const consumeCallCount = (store: CallCounterStore, keySuffix: string) => {
  const currentTime = Date.now();
  const { firstCallTime, count } = store.get(keySuffix);

  if (!firstCallTime || currentTime - firstCallTime > TIME_FRAME) {
    store.set(keySuffix, { firstCallTime: currentTime, count: 1 });
    return true;
  }
  if (count >= CALL_LIMIT) return false;

  store.set(keySuffix, { firstCallTime, count: count + 1 });
  return true;
};

const isWithinCallLimit = (keySuffix: string) => {
  if (typeof window === "undefined") return true;

  try {
    return consumeCallCount(sessionCallCounterStore, keySuffix);
  } catch {
    return consumeCallCount(memoryCallCounterStore, keySuffix);
  }
};

const getCallLimitKeySuffix = ({
  error,
  isExpectedError,
  callLimitGroup,
}: {
  error: string | Error;
  isExpectedError: boolean;
  callLimitGroup?: CallLimitGroup;
}) => {
  if (isExpectedError) return EXPECTED_ERROR_SESSION_KEY_SUFFIX;
  if (error instanceof Error && error.name === "ApiForbiddenError") return FORBIDDEN_ERROR_SESSION_KEY_SUFFIX;
  // 通信断も同じ枠で数え、グループの報告全体を CALL_LIMIT 件に収める
  return callLimitGroup ? `:${callLimitGroup}` : "";
};

export const createErrorHandler: CreateErrorHandler = () => {
  if (process.env.NODE_ENV !== "production") {
    return localErrorHandler;
  }

  const errorHandler = new StackdriverErrorReporter();
  // start() 前に report() を呼ぶと this.context 未初期化で TypeError になるため、start 済みかを保持する
  let isErrorHandlerStarted = false;
  const firebaseApiKey = process.env.NEXT_PUBLIC_FIREBASE_API_KEY ?? process.env.FIREBASE_API_KEY;
  const firebaseProjectId = process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID ?? process.env.FIREBASE_PROJECT_ID;
  const stackdriverServiceName =
    process.env.NEXT_PUBLIC_STACKDRIVER_SERVICE_NAME ?? process.env.STACKDRIVER_SERVICE_NAME;
  // 未取得時は Stackdriver を起動しない判定に使うため、"unknown" へフォールバックしない
  const appVersion = process.env.NEXT_PUBLIC_APP_VERSION ?? process.env.npm_package_version;

  if (typeof window !== "undefined" && firebaseApiKey && firebaseProjectId && stackdriverServiceName && appVersion) {
    errorHandler.start({
      // Stackdriver API Key
      key: firebaseApiKey,

      // GCP Project ID
      projectId: firebaseProjectId,

      // The following optional arguments can also be provided:

      service: stackdriverServiceName,
      // Name of the service reporting the error, defaults to 'web'.

      version: appVersion,
      // Version identifier of the service reporting the error.

      // reportUncaughtExceptions: false
      // Set to false to prevent reporting unhandled exceptions, default: `true`.

      reportUnhandledPromiseRejections: false,
      // Set to false to prevent reporting unhandled promise rejections, default: `true`.

      // disabled: true
      // Set to true to not send error reports, this can be used when developing locally, default: `false`.

      // context: { user: userId }
      // You can set the user later using setUser()
    });
    isErrorHandlerStarted = true;
  }

  return async (rawError, options) => {
    try {
      const error = await toReportableError(rawError);
      const isExpectedError = isExpectedErrorForSentry(error);
      const callLimitGroup = options?.callLimitGroup;
      const isGroupNetworkError = !!callLimitGroup && isNetworkError(error);

      if (!isWithinCallLimit(getCallLimitKeySuffix({ error, isExpectedError, callLimitGroup }))) {
        logger.debug("Error handler call limit reached");
        return;
      }

      if (isSentryEnabled() && !options?.skipSentry && !isExpectedError) {
        Sentry.withScope((scope) => {
          if (options?.extra) scope.setExtras(options.extra);

          if (isGroupNetworkError) {
            // beforeSend で除外される例外ではなく固定のメッセージにし、1 つの Issue のイベント数で件数を数える
            scope.setTags({ ...options?.tags, "network.error": "true" });
            scope.setFingerprint(["network-error", callLimitGroup]);
            Sentry.captureMessage(`Network error: ${callLimitGroup}`, "info");
            return;
          }

          if (options?.tags) scope.setTags(options.tags);

          if (error instanceof Error) {
            Sentry.captureException(error);
          } else {
            Sentry.captureMessage(error, "error");
          }
        });
      }
      if (isErrorHandlerStarted) {
        await errorHandler.report(toStackdriverError(error), options);
      } else {
        await localErrorHandler(error, options);
      }
    } catch (e) {
      logger.error(e);
    }
  };
};
